// packages/shared/src/rules/placement.ts
import type { CatalogEntry, CatalogId, Rot, ShadowSize } from '@whiskerton/content';
import type { Rect, TileXY } from '../types/geom';
import type { Plot } from '../types/map';
import { tankFootprint } from './aquarium';
import { catalogLookup, type Lookup } from './lookup';

export interface Placed { id: string; catalogId: CatalogId; x: number; y: number; rot: Rot; size?: number }

function baseFootprint(e: CatalogEntry | undefined, size: number | undefined): readonly [number, number] {
  if (e?.kind === 'furniture') return e.footprint;
  if (e?.kind === 'fish') return tankFootprint((size ?? e.shadow) as ShadowSize);
  return [1, 1];
}
export function footprintOf(p: Pick<Placed, 'catalogId' | 'rot' | 'size'>, lookup: Lookup = catalogLookup): { w: number; d: number } {
  const [w, d] = baseFootprint(lookup(p.catalogId), p.size);
  return p.rot % 2 ? { w: d, d: w } : { w, d };
}
const rectOf = (p: Placed, lookup: Lookup): Rect => { const { w, d } = footprintOf(p, lookup); return { x: p.x, y: p.y, w, h: d }; };
const overlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const covers = (r: Rect, t: TileXY) => t.x >= r.x && t.x < r.x + r.w && t.y >= r.y && t.y < r.y + r.h;
const isRug = (id: CatalogId, lookup: Lookup) => { const e = lookup(id); return e?.kind === 'furniture' && e.category === 'rug'; };
const tilesOf = (r: Rect): TileXY[] => { const out: TileXY[] = []; for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) out.push({ x, y }); return out; };
function collides(placed: readonly Placed[], cand: Placed, lookup: Lookup): boolean {
  const r = rectOf(cand, lookup), rug = isRug(cand.catalogId, lookup);
  return placed.some((p) => p.id !== cand.id && isRug(p.catalogId, lookup) === rug && overlaps(rectOf(p, lookup), r));
}

export function checkPlacement(room: { w: number; h: number; door: TileXY }, placed: readonly Placed[], cand: Placed, lookup: Lookup = catalogLookup):
  'overlap' | 'out_of_bounds' | 'door_tile' | 'not_placeable' | null {
  const e = lookup(cand.catalogId);
  if (e?.kind !== 'furniture' && e?.kind !== 'fish') return 'not_placeable';
  const r = rectOf(cand, lookup);
  if (r.x < 0 || r.y < 0 || r.x + r.w > room.w || r.y + r.h > room.h) return 'out_of_bounds';
  if (covers(r, room.door)) return 'door_tile';
  return collides(placed, cand, lookup) ? 'overlap' : null;
}

export function checkYardPlacement(plot: Plot, placed: readonly Placed[], isBlocked: (t: TileXY) => boolean, cand: Placed, lookup: Lookup = catalogLookup):
  'overlap' | 'out_of_bounds' | 'not_your_yard' | 'not_placeable' | null {
  const e = lookup(cand.catalogId);
  if (e?.kind !== 'furniture' || !e.outdoor) return 'not_placeable';
  const r = rectOf(cand, lookup);
  if (r.x < 0 || r.y < 0) return 'out_of_bounds';
  const tiles = tilesOf(r);
  if (tiles.some((t) => !covers(plot.rect, t) || covers(plot.house, t))) return 'not_your_yard';
  const fixed = (t: TileXY) => (t.x === plot.door.x && t.y === plot.door.y) || (t.x === plot.mailbox.x && t.y === plot.mailbox.y);
  if (tiles.some((t) => fixed(t) || isBlocked(t))) return 'overlap';
  return collides(placed, cand, lookup) ? 'overlap' : null;
}
